import { useTranslation } from "react-i18next";
import { Mic } from "lucide-react";
import { useSpeechDictation } from "../features/dictation/useSpeechDictation";

interface DictationButtonProps {
  /** Called with each finished spoken phrase — append it to the target field. */
  onText: (text: string) => void;
  disabled?: boolean;
  /** Icon-only round button, for sitting inside a text field's corner. */
  compact?: boolean;
}

/**
 * Mic toggle for text fields, backed by the browser's speech recognition.
 * Renders nothing where the browser doesn't support it (e.g. Firefox).
 */
const DictationButton = ({ onText, disabled, compact }: DictationButtonProps) => {
  const { t } = useTranslation();
  const { supported, listening, denied, start, stop } = useSpeechDictation(onText);

  if (!supported) {
    return null;
  }

  const label = listening
    ? t("common.dictationStop", "Listening… tap to stop")
    : t("common.dictate", "Dictate");

  if (compact) {
    return (
      <button
        type="button"
        className="rounded-full p-1.5 text-grey-40 hover:bg-grey-95 hover:text-grey-20 dark:text-grey-80 dark:hover:bg-primary-20 dark:hover:text-white"
        onClick={listening ? stop : start}
        disabled={disabled}
        aria-pressed={listening}
        aria-label={label}
        title={denied ? t("common.dictationDenied", "Microphone access was denied.") : label}
      >
        <Mic
          size={18}
          strokeWidth={2}
          className={listening ? "animate-pulse text-red-600 dark:text-red-400" : undefined}
        />
      </button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={listening ? stop : start}
        disabled={disabled}
        aria-pressed={listening}
      >
        <Mic
          size={20}
          strokeWidth={2}
          className={listening ? "animate-pulse text-red-600 dark:text-red-400" : undefined}
        />
        <span>{label}</span>
      </button>
      {denied && (
        <span className="text-sm text-red-600 dark:text-red-400">
          {t("common.dictationDenied", "Microphone access was denied.")}
        </span>
      )}
    </span>
  );
};

export default DictationButton;
